/**
 * windowKeyboard — which window has the keyboard (PQR-004 / PQR-005, 2026-10-07).
 *
 * The app's windows come from two families: MUI dialogs (`HubModal` and the
 * raw `<Dialog>`s) and the hand-portaled ones (the record windows on
 * `DetailModalShell`, the email composer, a few dozen smaller overlays). MUI
 * used to keep the keyboard for its own dialogs by force — which is what made
 * a window opened on top of a dialog impossible to type in, so that force is
 * off (`theme.ts` `dialogFocus`). Two rules replace it:
 *
 *   1. A window takes the keyboard when it opens and gives it back when it
 *      closes (`useOwnsKeyboard`, used by `DetailModalShell`).
 *   2. The keyboard never rests on nothing while a window is open. When the
 *      window on top goes away without handing focus to anyone — most
 *      hand-portaled overlays do not — the window now on top gets it
 *      (`installKeyboardKeeper`). Without this, closing the email composer
 *      over the Call Crusher left focus on <body>, and the Crusher ignored
 *      Escape until it was clicked.
 *
 * Together: Escape always belongs to the window you are looking at.
 */
import { useEffect, useRef } from 'react';

/** What can hold the keyboard, in document order: an MUI dialog's focus
 *  container, and a record window's scrim (`DetailModalShell`). Portals are
 *  appended to <body> as they open, so the last one is the one on top. */
const WINDOWS = '.MuiDialog-container, [role="dialog"][aria-modal="true"][tabindex="-1"]';

function topWindow(): HTMLElement | null {
  const open = document.querySelectorAll<HTMLElement>(WINDOWS);
  for (let i = open.length - 1; i >= 0; i -= 1) {
    // A kept-mounted dialog that is closed is still in the document, hidden.
    if (getComputedStyle(open[i]).visibility !== 'hidden') return open[i];
  }
  return null;
}

/** If nothing has the keyboard, hand it to the window on top. */
export function reclaimKeyboard(): void {
  const active = document.activeElement;
  if (active && active !== document.body) return;
  topWindow()?.focus({ preventScroll: true });
}

let keeperInstalled = false;

/** One observer for the whole app, installed the first time any window or
 *  themed page renders. It only ever acts when focus is on <body>. */
export function installKeyboardKeeper(): void {
  if (keeperInstalled || typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
  keeperInstalled = true;
  new MutationObserver((records) => {
    if (records.some((r) => r.removedNodes.length > 0)) reclaimKeyboard();
  }).observe(document.body, { childList: true });
}

/**
 * A window takes the keyboard when it opens and gives it back when it closes.
 *   - On open, focus moves to the window, unless something inside it already
 *     took focus (a field that focuses itself keeps the caret).
 *   - On close, focus returns to whatever had it — unless something else has
 *     taken it since, which is then left alone.
 * Put the ref on the window's outermost element and give it `tabIndex={-1}`,
 * so a click anywhere inside also lands focus in the window.
 */
export function useOwnsKeyboard<T extends HTMLElement = HTMLDivElement>(): React.RefObject<T> {
  const ref = useRef<T>(null);
  useEffect(() => {
    installKeyboardKeeper();
    const el = ref.current;
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
    return () => {
      const now = document.activeElement;
      const stillOurs = !now || now === document.body || (el != null && el.contains(now));
      if (stillOurs && before && before !== document.body && before.isConnected) before.focus({ preventScroll: true });
    };
  }, []);
  return ref;
}
